/**
 * Over-the-air (OTA) update runtime.
 *
 * Self-managed live updates via `@capawesome/capacitor-live-update`, driven by
 * our own `/ota` backend (not Capawesome Cloud). Flow: on launch we ask the
 * backend for the active release for the device's channel, gate on the rollout
 * cohort, download the bundle zip, and stage it as the *next* bundle - so it
 * applies on the next cold start (non-blocking). The user can also apply
 * immediately ("Restart now").
 *
 * Anti-rollback: main.tsx calls `LiveUpdate.ready()` at boot within the
 * `readyTimeout` configured in capacitor.config.ts; a bundle that never reaches
 * `ready()` is auto-reverted by the plugin.
 */
import { LiveUpdate } from '@capawesome/capacitor-live-update';

import { CONFIG } from 'src/global-config';
import { type OtaRelease, fetchActiveRelease } from 'src/actions/ota';

import { isNativePlatform } from './capacitor';

// ----------------------------------------------------------------------

const CHECK_THROTTLE_MS = 5 * 60 * 1000; // don't re-hit the backend more than once / 5 min
const LAST_CHECK_KEY = 'artham.ota.lastCheckAt';

export type OtaCheckResult =
  | { available: true; release: OtaRelease }
  | { available: false; reason: 'up-to-date' | 'not-native' | 'no-release' | 'not-in-cohort' | 'throttled' };

/** OTA only runs on a native build in production - it is a no-op on web/dev. */
export function isOtaEnabled(): boolean {
  return isNativePlatform() && import.meta.env.PROD;
}

/** Map the signed-in user's role to a backend release channel. */
export function getChannelForRole(userRole?: string | null): string {
  switch (userRole) {
    case 'DEALER':
      return 'dealer';
    case 'DISTRIBUTOR':
      return 'distributor';
    case 'ADMIN':
      return 'admin';
    default:
      return 'all';
  }
}

/**
 * The version currently running: the OTA bundle id if one is applied, otherwise
 * the built-in bundle shipped in the binary (`CONFIG.appVersion`).
 */
export async function getCurrentVersion(): Promise<{ version: string; isOta: boolean }> {
  if (!isNativePlatform()) return { version: CONFIG.appVersion, isOta: false };
  try {
    const { bundleId } = await LiveUpdate.getCurrentBundle();
    if (bundleId) return { version: bundleId, isOta: true };
  } catch {
    // fall through to the built-in version
  }
  return { version: CONFIG.appVersion, isOta: false };
}

/** Notify the plugin the app booted successfully so it doesn't roll back. */
export async function markAppReady(): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    const { rollback } = await LiveUpdate.ready();
    if (rollback) {
      console.warn('[ota] app was rolled back to the previous bundle');
    }
  } catch (err) {
    console.warn('[ota] ready() failed', err);
  }
}

// ----------------------------------------------------------------------

/** Compare dotted numeric versions. Returns true when `next` is strictly newer. */
function isNewer(next: string, current: string): boolean {
  const a = next.split('.').map((n) => parseInt(n, 10) || 0);
  const b = current.split('.').map((n) => parseInt(n, 10) || 0);
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i += 1) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (x !== y) return x > y;
  }
  return false;
}

/** Stable 0-99 bucket for the device, so a % rollout is deterministic per device. */
async function getRolloutBucket(): Promise<number> {
  let id = '';
  try {
    id = (await LiveUpdate.getDeviceId()).deviceId ?? '';
  } catch {
    id = '';
  }
  if (!id) {
    // Fall back to a persisted random id so cohort membership stays stable.
    id = localStorage.getItem('artham.ota.deviceId') ?? '';
    if (!id) {
      id = Math.random().toString(36).slice(2);
      localStorage.setItem('artham.ota.deviceId', id);
    }
  }
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    // Math.imul keeps the multiply in 32-bit int space without a bitwise op.
    hash = (Math.imul(hash, 31) + id.charCodeAt(i)) % 1_000_000_007;
  }
  return Math.abs(hash) % 100;
}

/**
 * Ask the backend whether a newer bundle is available for this device's channel
 * and rollout cohort. Does not download anything.
 */
export async function checkForUpdate(opts: {
  channel: string;
  force?: boolean;
}): Promise<OtaCheckResult> {
  if (!isOtaEnabled()) return { available: false, reason: 'not-native' };

  if (!opts.force) {
    const last = parseInt(localStorage.getItem(LAST_CHECK_KEY) ?? '0', 10);
    if (last && Date.now() - last < CHECK_THROTTLE_MS) {
      return { available: false, reason: 'throttled' };
    }
  }
  localStorage.setItem(LAST_CHECK_KEY, String(Date.now()));

  const release = await fetchActiveRelease(opts.channel);
  if (!release || !release.bundleUrl) return { available: false, reason: 'no-release' };

  const { version: current } = await getCurrentVersion();
  if (!isNewer(release.version, current)) return { available: false, reason: 'up-to-date' };

  const rolloutPct = release.rolloutPct ?? release.rolloutPercent ?? 0;
  if (rolloutPct < 100) {
    const bucket = await getRolloutBucket();
    if (bucket >= rolloutPct) return { available: false, reason: 'not-in-cohort' };
  }

  return { available: true, release };
}

/**
 * Download the release bundle and stage it as the next bundle. It boots on the
 * next cold start, or immediately after `applyNow()`.
 */
export async function downloadAndStageUpdate(release: OtaRelease): Promise<void> {
  if (!release.bundleUrl) throw new Error('release has no bundleUrl');
  await LiveUpdate.downloadBundle({
    url: release.bundleUrl,
    bundleId: release.version,
    artifactType: 'zip',
  });
  await LiveUpdate.setNextBundle({ bundleId: release.version });
}

/** Reload the webview into the staged bundle right now. */
export async function applyNow(): Promise<void> {
  await LiveUpdate.reload();
}
